import { useMemo } from 'react'
import {
  getRestrictedBedbankCancellationSplit,
  isRestrictedBedbankPolicyEligibleRate,
  type RestrictedBedbankEligibilityRate,
} from 'lib/offer/cancellationPolicyUtils'
import useRestrictedCancellationView from 'hooks/useRestrictedCancellationView'
import type { RestrictedCancellationPolicyView } from 'hooks/useRestrictedCancellationView'

const EMPTY_DESCRIPTION: Array<string> = []

interface UseRestrictedBedbankCancellationPolicyOptions {
  /** Gate on visibility so exposure only counts users who saw the policy. */
  canActivate?: boolean
  /** Shared clock, so two surfaces derive one window. */
  now?: Date
}

/** Undefined for control, ineligible rates, or a deadline inside the seven-day window. */
function useRestrictedBedbankCancellationPolicy(
  rate: RestrictedBedbankEligibilityRate | undefined,
  cancellationPolicy: App.BedbankCancellationPolicy | undefined,
  options?: UseRestrictedBedbankCancellationPolicyOptions,
): RestrictedCancellationPolicyView | undefined {
  const eligible = isRestrictedBedbankPolicyEligibleRate(rate)

  const split = useMemo(() => {
    if (!eligible || !cancellationPolicy) return undefined
    return getRestrictedBedbankCancellationSplit(cancellationPolicy, options?.now ?? new Date())
  }, [eligible, cancellationPolicy, options?.now])

  const controlDescription = cancellationPolicy?.description ?? EMPTY_DESCRIPTION

  return useRestrictedCancellationView(split, controlDescription, { canActivate: options?.canActivate })
}

export default useRestrictedBedbankCancellationPolicy
